import React from 'react';
import PropTypes from 'prop-types';
import {
  Card, Typography, Button, Table, Row,
  Col,
} from 'antd';
import Qty from 'js-quantities';
import moment from 'moment';
import {
  ReloadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import Deployment from '../deployments/Deployment';
import ConnectionChart from './ConnectionChart';
import PlatformEvent from '../PlatformEvent';
import DataRangeSelect from '../DataRangeSelect';
import PlatformMessage from './PlatformMessage';
import ConnectionStatus from '../ConnectionStatus';

const qartodResultMap = {
  1: {
    label: 'Pass',
    icon:
  <div
    style={{
      background: '#49AA19',
      width: 12,
      height: 12,
      border: '1px solid #80C45E',
      borderRadius: '50%',
    }}
  />,
  },
  2: {
    label: 'Not Evaluated',
    icon:
  <div
    style={{
      background: '#6E7B8E',
      width: 12,
      height: 12,
      border: '1px solid #6E7B8E',
      borderRadius: '50%',
    }}
  />,
  },
  3: {
    label: 'Suspect',
    icon:
  <div
    style={{
      background: '#D89614',
      width: 12,
      height: 12,
      border: '1px solid #E4B65B',
      borderRadius: '50%',
    }}
  />,
  },
  4: {
    label: 'Fail',
    icon:
  <div
    style={{
      background: '#DC4446',
      width: 12,
      height: 12,
      border: '1px solid #E77C7E',
      borderRadius: '50%',
    }}
  />,
  },
  9: {
    label: 'Missing',
    icon:
  <div
    style={{
      background: '#D9E0E4',
      width: 12,
      height: 12,
      border: '1px solid #E4E9EC',
      borderRadius: '50%',
    }}
  />,
  },
};

const getQartodLabel = (code) => {
  const result = qartodResultMap[code] || qartodResultMap[9];

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {result.icon}
      <span>{result.label}</span>
    </span>
  );
};

const Dashboard = (props) => {
  const {
    latestObservations,
    obsDataV2,
    obsDatasetSummary,
    onObsDateRangeChange,
    refetchObsLatest,
    obsDataV2IsLoading,
  } = props;

  // TODO: look up user unit prefs
  const getValue = (paramObs) => {
    const value = paramObs?.observations?.[0]?.value;
    if (value == null) return '—';

    const canonicalUnit = paramObs?.parameterConfig?.units.find((u) => u.id === paramObs?.parameterConfig?.canonical_unit_id);
    const preferredUnit = paramObs?.parameterConfig?.units.find((u) => u.id === paramObs?.parameterConfig?.preferred_imperial_unit_id);
    try {
      const converted = Qty(value, canonicalUnit?.js_qty_unit).to(preferredUnit?.js_qty_unit);
      return `${converted.scalar.toFixed(2)} ${preferredUnit?.symbol}`;
    } catch (e) {
      return `${value} ${canonicalUnit?.symbol || ''}`;
    }
  };

  // TODO: use utils version here
  const getDisplayName = (parameterConfig, parameter) => {
    const displayNameBase = parameterConfig?.display_name?.en || parameter?.standard_name || 'Unknown';

    if (parameterConfig?.standard_name === 'sea_water_temperature' && (parameter?.depth === 0 || !parameter?.depth)) {
      return `${displayNameBase} (Surface)`;
    }

    if (parameter?.depth && parameter.depth !== 0) {
      return `${displayNameBase} (${parameter.depth}M)`;
    }

    return displayNameBase;
  };

  const latestTimestamp = latestObservations.reduce((latest, item) => {
    const itemLatest = item.observations.reduce((maxTs, obs) => Math.max(maxTs, obs.timestamp), -Infinity);
    return Math.max(latest, itemLatest);
  }, -Infinity);

  const rows = latestObservations.flatMap((item) => {
    const parameterName = getDisplayName(item.parameterConfig, item.parameter);
    return item.observations.map((obs, index) => ({
      key: `${item.parameter_id}-${index}`,
      depth: item?.parameter?.depth,
      parameter: parameterName,
      value: getValue({ ...item, observations: [obs] }),
      qartod: getQartodLabel(obs.qartod),
      timestamp: obs.timestamp,
      parameter_id: item.parameter_id,
    }));
  });

  const columns = [
    {
      title: 'Parameter',
      dataIndex: 'parameter',
      key: 'parameter',
      render: (text, record) => (
        <Link
          to={`/data-console/manage/${obsDatasetSummary?.obs_dataset_id}/parameters/${record?.parameter_id}`}
          style={{
            color: '#D9E0E4',
          }}
        >
          {text}
        </Link>
      ),
      sorter: (a, b) => a.parameter.localeCompare(b.parameter),
    },
    {
      title: 'Observation',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Depth (m)',
      dataIndex: 'depth',
      key: 'depth',
      defaultSortOrder: 'ascend',
      render: (depth) => depth || 'Surface',
      sorter: (a, b) => a.depth - b.depth,
    },
    {
      title: 'QARTOD',
      dataIndex: 'qartod',
      key: 'qartod',
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => moment.unix(timestamp).format('MM/DD/YYYY HH:mm:ss A'),
      sorter: (a, b) => a.timestamp - b.timestamp,
    },
  ];

  return (
    <div>
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <Typography.Title
            level={4}
            style={{
              color: '#fff',
              marginBottom: '0.2em',
            }}
          >
            System Health
          </Typography.Title>
          <DataRangeSelect onChange={onObsDateRangeChange} />
        </div>
        <Row gutter={16}>
          <Col xs={24} sm={24} lg={12}>
            <Card
              // variant="borderless"
              bordered={false}
              title={(
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  Deployment
                  <PlatformEvent platformEvent={obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_event?.event} />
                </div>
              )}
              style={{
                backgroundColor: '#03304b', // TODO: replace with brand var
                border: '1px solid #4f6f81',
              }}
            >
              <Deployment obsDatasetSummary={obsDatasetSummary} />
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={12}>
            <Card
              // variant="borderless"
              title={(
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  Connection
                  <ConnectionStatus
                    latestTimestamp={latestTimestamp}
                  />
                </div>
              )}
              bordered={false}
              style={{
                backgroundColor: '#03304b', // TODO: replace with brand var
                border: '1px solid #4f6f81',
              }}
            >
              <ConnectionChart timestamps={obsDataV2?.timestamps} isLoading={obsDataV2IsLoading} />
            </Card>
          </Col>
        </Row>
        <div
          style={{
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '10px 0',
            }}
          >
            <Typography.Title
              level={4}
              style={{
                color: '#fff',
                marginBottom: '0.2em',
              }}
            >
              Latest Observations
            </Typography.Title>
            <Button
              icon={<ReloadOutlined />}
              className="action-button"
              onClick={refetchObsLatest}
            >
              Refresh
            </Button>
          </div>
          <Table
            className="platform-dashboard-table"
            columns={columns}
            dataSource={rows}
            pagination={false}
          />
        </div>
        <PlatformMessage
          obsDatasetSummary={obsDatasetSummary}
        />
      </div>
    </div>
  );
};

Dashboard.propTypes = {
  latestObservations: PropTypes.arrayOf(PropTypes.object),
  obsDataV2: PropTypes.array,
  obsDatasetSummary: PropTypes.object,
  onObsDateRangeChange: PropTypes.func,
  refetchObsLatest: PropTypes.func,
  obsDataV2IsLoading: PropTypes.bool,
};

Dashboard.defaultProps = {
  latestObservations: [],
  obsDataV2: [],
  obsDatasetSummary: {},
  onObsDateRangeChange: () => {},
  refetchObsLatest: () => {},
  obsDataV2IsLoading: false,
};

export default Dashboard;
